import React from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaBuilding, FaWarehouse, FaChevronRight } from 'react-icons/fa';

const Contact = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const formControls = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  const locations = [
    {
      id: 'corporate',
      name: 'Corporate Office',
      address: 'Office No. 411, 4th Floor, Landmark Centre, behind Bhapkar Petrol Pump, Pune-Satara Road, Pune – 411009.',
      phone: '+91 7588 289989',
      email: 'admin@migliorelifesciences.in',
      icon: <FaBuilding className="text-2xl text-primary" />
    }
  ];

  return (
    <div>
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary-dark text-white py-24">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl text-gray-100 max-w-3xl mb-8">
              Get in touch with Migliore Life Sciences. We'd love to hear from you 
              and discuss how our pharmaceutical solutions can meet your needs.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:+917588289989" 
                className="inline-flex items-center bg-white text-primary px-4 py-3 rounded-md font-medium hover:bg-gray-100 transition-colors"
              >
                <FaPhone className="mr-2" /> Call Us
              </a>
              <a 
                href="mailto:admin@migliorelifesciences.in" 
                className="inline-flex items-center bg-transparent text-white border border-white px-4 py-3 rounded-md font-medium hover:bg-white/10 transition-colors"
              >
                <FaEnvelope className="mr-2" /> Email Us
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Contact Form and Info */}
      <div className="container-custom py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Get In Touch</h2>
          <div className="w-24 h-1 bg-primary mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We're here to answer any questions you may have about our pharmaceutical products
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="order-2 lg:order-1 bg-white p-8 rounded-lg shadow-lg"
          >
            <h3 className="text-2xl font-bold mb-6 text-primary">Send Us a Message</h3>
            <form className="space-y-6">
              <motion.div 
                variants={formControls} 
                transition={{ delay: 0.1 }}
              >
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Your name"
                  className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                />
              </motion.div>
              
              <motion.div 
                variants={formControls}
                transition={{ delay: 0.2 }}
              >
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Your email address"
                  className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                />
              </motion.div>
              
              <motion.div 
                variants={formControls}
                transition={{ delay: 0.3 }}
              >
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="Your phone number"
                  className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </motion.div>
              
              <motion.div 
                variants={formControls}
                transition={{ delay: 0.4 }}
              >
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <select
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                >
                  <option value="">Select a subject</option>
                  <option value="general">General Inquiry</option>
                  <option value="product">Product Information</option>
                  <option value="distribution">Distribution Partnership</option>
                  <option value="careers">Careers</option>
                  <option value="other">Other</option>
                </select>
              </motion.div>
              
              <motion.div 
                variants={formControls}
                transition={{ delay: 0.5 }}
              >
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  placeholder="How can we help you?"
                  className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                ></textarea>
              </motion.div>
              
              <motion.div 
                variants={formControls}
                transition={{ delay: 0.6 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <button
                  type="submit"
                  className="w-full bg-primary text-white font-bold py-3 px-6 rounded-md hover:bg-primary-dark transition-colors"
                >
                  Send Message
                </button>
              </motion.div>
            </form>
          </motion.div>
          
          {/* Contact Information */}
          <motion.div
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="order-1 lg:order-2"
          >
            <h3 className="text-2xl font-bold mb-6 text-primary">Our Locations</h3>
            
            <div className="space-y-8">
              {locations.map((location) => (
                <motion.div 
                  key={location.id} 
                  className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all"
                  whileHover={{ y: -5 }}
                >
                  <div className="flex items-center mb-4">
                    {location.icon}
                    <h3 className="text-xl font-bold ml-3">{location.name}</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex">
                      <FaMapMarkerAlt className="text-primary mt-1 flex-shrink-0" />
                      <p className="ml-3 text-gray-600">{location.address}</p>
                    </div>
                    
                    <div className="flex">
                      <FaPhone className="text-primary flex-shrink-0" />
                      <a 
                        href={`tel:${location.phone}`} 
                        className="ml-3 text-gray-600 hover:text-primary transition-colors"
                      >
                        {location.phone}
                      </a>
                    </div>
                    
                    <div className="flex">
                      <FaEnvelope className="text-primary flex-shrink-0" />
                      <a 
                        href={`mailto:${location.email}`} 
                        className="ml-3 text-gray-600 hover:text-primary transition-colors"
                      >
                        {location.email}
                      </a>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Map Section */}
      <div className="bg-gray-50 py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Visit Our Office</h2>
            <div className="w-24 h-1 bg-primary mx-auto mb-8"></div>
          </motion.div>

          <div className="bg-white p-4 rounded-lg shadow-lg">
            <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
              {/* This would normally be an embedded Google Map iframe */}
              <div className="w-full h-96 bg-gray-200 flex items-center justify-center">
                <div className="text-center p-8">
                  <FaMapMarkerAlt className="text-primary text-4xl mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">Google Maps Placeholder</h3>
                  <p className="text-gray-600 mb-4">A Google Maps embed would normally appear here showing our office location.</p>
                  <a 
                    href="https://goo.gl/maps/YourActualMapsLink" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-primary font-medium hover:underline"
                  >
                    View on Google Maps <FaChevronRight className="ml-1" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="container-custom py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
          <div className="w-24 h-1 bg-primary mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Find answers to common questions about our pharmaceutical products and services
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          <div className="space-y-6">
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="text-xl font-bold mb-3">How can I order Migliore products?</h3>
              <p className="text-gray-600">
                Migliore Life Sciences products are available through licensed healthcare professionals and authorized distributors. Please contact us for information on how to place an order or find a distributor near you.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="text-xl font-bold mb-3">How can I become a distributor?</h3>
              <p className="text-gray-600">
                If you're interested in becoming an authorized distributor for Migliore Life Sciences products, please contact our business development team through the form above or email us directly.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <h3 className="text-xl font-bold mb-3">What are your business hours?</h3>
              <p className="text-gray-600">
                Our corporate office is open Monday through Friday from 9:00 AM to 6:00 PM IST. Our depot operates from 8:00 AM to 7:00 PM IST on weekdays and 9:00 AM to 1:00 PM on Saturdays.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
